const SEARCH_PAGE_GROUPS = new Set([
  'projects',
  'news',
  'lab',
  'collections',
  'reels',
  'articles',
]);
const SEARCH_PAGE_SUBMIT_DELAY = 400;

function normalizeSearchPageGroup(hash = '') {
  const group = String(hash).replace(/^#/, '').toLowerCase();
  return SEARCH_PAGE_GROUPS.has(group) ? group : 'all';
}

function normalizeSearchPageQuery(query = '') {
  return String(query).trim().replace(/\s+/g, ' ').toLowerCase();
}

function appendSearchGroupHash(url, group) {
  const normalizedGroup = normalizeSearchPageGroup(`#${group}`);
  const cleanUrl = String(url).split('#')[0];
  return normalizedGroup === 'all' ? cleanUrl : `${cleanUrl}#${normalizedGroup}`;
}

function getNextSearchPageResultIndex(currentIndex, direction, resultCount) {
  if (resultCount === 0) return -1;
  if (direction === 'up') return currentIndex <= 0 ? resultCount - 1 : currentIndex - 1;

  return currentIndex < 0 || currentIndex >= resultCount - 1 ? 0 : currentIndex + 1;
}

class CommandSearchPage extends HTMLElement {
  constructor() {
    super();
    this.searchSubmitTimer = null;
    this.handleHashChange = this.handleHashChange.bind(this);
    this.handleCompositionEnd = this.handleCompositionEnd.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handlePointerOver = this.handlePointerOver.bind(this);
    this.handleFocusIn = this.handleFocusIn.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  connectedCallback() {
    this.input = this.querySelector('[data-search-page-input]');
    this.clearButton = this.querySelector('[data-search-page-clear]');
    this.form = this.querySelector('[data-search-page-form]');
    this.initialQuery = normalizeSearchPageQuery(this.input?.defaultValue);

    window.addEventListener('hashchange', this.handleHashChange);
    this.input?.addEventListener('input', this.handleInput);
    this.input?.addEventListener('compositionend', this.handleCompositionEnd);
    this.form?.addEventListener('submit', this.handleSubmit);
    this.addEventListener('keydown', this.handleKeydown);
    this.addEventListener('pointerover', this.handlePointerOver);
    this.addEventListener('focusin', this.handleFocusIn);

    this.syncActiveGroup();
    this.toggleClearButton();
  }

  disconnectedCallback() {
    this.cancelSearchSubmit();
    window.removeEventListener('hashchange', this.handleHashChange);
    this.input?.removeEventListener('input', this.handleInput);
    this.input?.removeEventListener('compositionend', this.handleCompositionEnd);
    this.form?.removeEventListener('submit', this.handleSubmit);
    this.removeEventListener('keydown', this.handleKeydown);
    this.removeEventListener('pointerover', this.handlePointerOver);
    this.removeEventListener('focusin', this.handleFocusIn);
  }

  handleHashChange() {
    this.syncActiveGroup();
  }

  handleCompositionEnd() {
    this.handleInput();
  }

  handleInput(event) {
    this.cancelSearchSubmit();
    if (event?.isComposing) return;
    this.toggleClearButton();
    const query = normalizeSearchPageQuery(this.input?.value);

    if (query.length === 0) {
      if (this.dataset.searchPagePerformed === 'true') this.resetToEmptyState();
      return;
    }

    if (query === this.initialQuery) return;
    this.searchSubmitTimer = window.setTimeout(() => this.submitSearch(query), SEARCH_PAGE_SUBMIT_DELAY);
  }

  handleSubmit() {
    this.cancelSearchSubmit();
  }

  syncActiveGroup() {
    const requestedGroup = normalizeSearchPageGroup(window.location.hash);
    const activeGroup =
      requestedGroup === 'all' ||
      this.querySelector(`[data-search-page-category="${requestedGroup}"]`)
        ? requestedGroup
        : 'all';
    this.dataset.activeGroup = activeGroup;
    this.clearSelection();

    this.querySelectorAll('[data-search-page-content] section[data-search-page-group]').forEach((section) => {
      section.hidden = activeGroup !== 'all' && section.dataset.searchPageGroup !== activeGroup;
    });

    this.querySelectorAll('[data-search-page-category]').forEach((category) => {
      if (category.dataset.searchPageCategory === activeGroup) {
        category.setAttribute('aria-current', 'page');
      } else {
        category.removeAttribute('aria-current');
      }
    });

    this.updatePagination(activeGroup);
    this.updateSummary(activeGroup);
  }

  handleKeydown(event) {
    if (event.isComposing || event.keyCode === 229) return;

    const category = event.target.closest?.('[data-search-page-category]');
    if (category && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
      const categories = [...this.querySelectorAll('[data-search-page-category]')];
      const currentIndex = categories.indexOf(category);
      const direction = event.key === 'ArrowLeft' ? -1 : 1;
      const nextCategory = categories[(currentIndex + direction + categories.length) % categories.length];

      event.preventDefault();
      nextCategory.focus();
      nextCategory.click();
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const rows = this.getVisibleRows();
    if (!rows.length) return;

    event.preventDefault();
    const currentIndex = rows.indexOf(event.target.closest?.('[data-search-page-row]'));
    const nextIndex = getNextSearchPageResultIndex(
      currentIndex,
      event.key === 'ArrowUp' ? 'up' : 'down',
      rows.length,
    );
    this.selectRow(rows[nextIndex], true);
  }

  handlePointerOver(event) {
    const row = event.target.closest?.('[data-search-page-row]');
    if (row && this.getVisibleRows().includes(row)) this.selectRow(row, false);
  }

  handleFocusIn(event) {
    const row = event.target.closest?.('[data-search-page-row]');
    if (row) this.selectRow(row, false);
  }

  getVisibleRows() {
    const activeGroup = this.dataset.activeGroup || 'all';

    return [...this.querySelectorAll('[data-search-page-row]')].filter((row) => {
      const group = row.closest('[data-search-page-group]');
      if (!group || group.hidden) return false;
      if (activeGroup === 'all' && row.closest('[data-search-page-preview-extra]')) return false;
      return true;
    });
  }

  selectRow(row, focus) {
    this.clearSelection();
    row.classList.add('is-selected');
    if (!focus) return;

    row.focus({ preventScroll: true });
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    row.scrollIntoView?.({
      block: 'nearest',
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  }

  clearSelection() {
    this.querySelectorAll('[data-search-page-row].is-selected').forEach((row) => {
      row.classList.remove('is-selected');
    });
  }

  cancelSearchSubmit() {
    if (this.searchSubmitTimer === null) return;
    window.clearTimeout(this.searchSubmitTimer);
    this.searchSubmitTimer = null;
  }

  submitSearch(query) {
    this.searchSubmitTimer = null;
    if (
      !this.form ||
      !this.input ||
      normalizeSearchPageQuery(this.input.value) !== query ||
      query === this.initialQuery
    ) {
      return;
    }

    if (typeof this.form.requestSubmit === 'function') {
      this.form.requestSubmit();
    } else {
      this.form.submit();
    }
  }

  resetToEmptyState() {
    if (!this.form) return;
    window.location.assign(this.form.action);
  }

  updatePagination(activeGroup) {
    this.querySelectorAll('[data-search-page-pagination] a').forEach((link) => {
      const baseUrl = link.dataset.searchPageBaseUrl || link.getAttribute('href');
      if (!baseUrl) return;
      link.dataset.searchPageBaseUrl = baseUrl.split('#')[0];
      link.setAttribute('href', appendSearchGroupHash(link.dataset.searchPageBaseUrl, activeGroup));
    });
  }

  updateSummary(activeGroup) {
    const summaries = [...this.querySelectorAll('[data-search-page-summary-copy]')];
    if (!summaries.length) return;

    const activeSummary =
      summaries.find((summary) => summary.dataset.searchPageSummaryCopy === activeGroup) ||
      summaries.find((summary) => summary.dataset.searchPageSummaryCopy === 'all');

    summaries.forEach((summary) => {
      summary.hidden = summary !== activeSummary;
    });
  }

  toggleClearButton() {
    if (!this.input || !this.clearButton) return;
    this.clearButton.hidden = this.input.value.length === 0;
  }
}

if (
  typeof customElements !== 'undefined' &&
  (typeof customElements.get !== 'function' || !customElements.get('command-search-page'))
) {
  customElements.define('command-search-page', CommandSearchPage);
}

if (typeof module !== 'undefined') {
  module.exports = {
    appendSearchGroupHash,
    CommandSearchPage,
    getNextSearchPageResultIndex,
    normalizeSearchPageGroup,
    normalizeSearchPageQuery,
  };
}
